import { documentStore, uiStore } from "@/store/store";
import { getDomElementFromTreeNode } from "@/lib/canvas/helpers";
import { forceNextTick } from "@/util/force-next-tick";
import { TreeNode } from "@/types/tree";
import { runCallbackIfElementIsNotVisibleInRoot } from "@/util/dom";

export function scrollToActiveElement() {
  // An element can be "activated" before it's visible on the page (like when we duplicate big elements)
  // Scroll to it after it's visible
  forceNextTick(() => {
    if (
      !uiStore.settings.autoScrollEnabled ||
      uiStore.isCanvasElementBeingDragged ||
      !uiStore.activeElement
    ) {
      return;
    }

    if (documentStore.document.documentMeta.postType === "breakdance_header") {
      return;
    }

    /**
       Chrome has a bug where only one scrollIntoView can be used at the same time when it has "smooth".
       If it's instant, multiple can be used, but we want the canvas scroll to be smooth, so we use "scrollTo" instead

       https://stackoverflow.com/questions/49318497/google-chrome-simultaneously-smooth-scrollintoview-with-more-elements-doesn
       https://bugs.chromium.org/p/chromium/issues/detail?id=833617
       */
    smoothScrollToElementOnCanvas(uiStore.activeElement);
  });
}

export function smoothScrollToLastElement() {
  const children = documentStore.document.tree.root.children;
  const lastSection = children[children.length - 1];
  smoothScrollToElementOnCanvas(lastSection);
}

export function smoothScrollToElementOnCanvas(element: TreeNode) {
  const domElement = getDomElementFromTreeNode(element);

  if (!domElement) return;

  const smoothScrollToActiveElementOnCanvas = () => {
    const iframe = document.getElementById("iframe") as HTMLIFrameElement;
    const elementRect = domElement.getBoundingClientRect();
    const canvasHeight =
      window.Breakdance.canvas.document.defaultView?.innerHeight ||
      window.Breakdance.canvas.document.documentElement.clientHeight;
    const elementHeightPlusTitlebar = elementRect.height + 20;
    const pageOffsetY =
      window.Breakdance.canvas.document.defaultView?.pageYOffset || 0;

    const isElementBiggerThanTheCanvas =
      elementHeightPlusTitlebar > canvasHeight;

    const elementTopPosition = pageOffsetY + elementRect.top;
    const elementCenteredPosition =
      elementTopPosition +
      // center the element
      (elementHeightPlusTitlebar - canvasHeight) / 2;

    iframe.contentWindow?.scrollTo({
      top: isElementBiggerThanTheCanvas
        ? // scroll to the top (minus the titlebar so it shows at the top)
          elementTopPosition - 40
        : elementCenteredPosition,
      behavior: "smooth"
    });
  };

  runCallbackIfElementIsNotVisibleInRoot(
    smoothScrollToActiveElementOnCanvas,
    domElement,
    /**
     * root: null is the default, I'm being explicit because the Canvas is an iframe
     * and IntersectionObserver will, by default, use  the "viewport" of the page
     * If an element is inside an iframe then it'll use the Iframe's "viewport"
     * *https://github.com/w3c/IntersectionObserver/issues/372#issuecomment-543938684
     **/
    { root: null }
  );
}
